/**
 * Procedural low-poly mesh builder for the engine's test creatures and props (art §3.2 layouts, §5.1 lofts):
 * a non-indexed 24 B/vertex render mesh (flat facets, barycentric corners, authored/automatic feature edges) and an
 * indexed 20 B/vertex inverted-hull mesh with smooth normals. Deterministic (hash-based irregularity, no Math.random).
 * P1 owns the real creature generator (`src/creature/mesh`); this one only feeds the engine until it lands.
 */
import type { CreatureMeshData } from '../../shared';

export type V3 = [number, number, number];
export interface Skin { a: number; b: number; w: number }
export const rigid = (bone: number): Skin => ({ a: bone, b: bone, w: 1 });
export const blend = (a: number, b: number, w: number): Skin => ({ a, b, w });

const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const mul = (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s];
const dot = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const len = (a: V3): number => Math.hypot(a[0], a[1], a[2]);
const norm = (a: V3): V3 => { const l = len(a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };
export const v3 = { add, sub, mul, dot, cross, len, norm };

/** Deterministic hash → [0, 1). */
export function hash3(a: number, b: number, c: number): number {
  let h = Math.imul(a | 0, 0x27d4eb2d) ^ Math.imul(b | 0, 0x165667b1) ^ Math.imul(c | 0, 0x9e3779b1);
  h ^= h >>> 15; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

interface Tri { p: [V3, V3, V3]; pat: [[number, number], [number, number], [number, number]]; skin: [Skin, Skin, Skin]; mat: number; region: number; edge: number; lw: number }

export interface LoftRing {
  /** Ring centre (H). */
  c: V3;
  /** Radius along the ring's U axis (partV 0 / 0.5) and F axis (partV 0.25 / 0.75). */
  ru: number; rf: number;
  skin: Skin;
  /** Optional per-side radius multiplier (side index → factor), e.g. a flattened face. */
  shape?: (v: number) => number;
  /** Optional offset added to each ring point (e.g. a muzzle bump), given partV. */
  bump?: (v: number) => V3;
}

export interface LoftSpec {
  rings: LoftRing[];
  sides: number;
  /** Direction of partV = 0 (projected perpendicular to the spine). */
  up: V3;
  /** Direction of partV = 0.25 (front). */
  front: V3;
  mat: number; region: number;
  stagger?: boolean;
  capStart?: boolean; capEnd?: boolean;
  /** Irregularity: ≤ this fraction of the local radius (art §5.1: 3 %). */
  jitter?: number;
  seed?: number;
  /** Mirror the jitter across X (symmetric parts). */
  mirrorX?: boolean;
  /** Feature-line weight for auto edges (0..2). */
  lineWeight?: number;
  /** Rings whose ring-edge gets an ink line (hems, cuffs). */
  hemRings?: number[];
  /** Hull line weight 0..1 (thin lines on small parts). */
  hullWeight?: number;
  /** Exclude from the hull (inner parts covered by others). */
  noHull?: boolean;
  /** Per-triangle material override by (ring index, side index) → material id or -1. */
  matAt?: (ring: number, side: number) => number;
  /** Rotates every ring by this fraction of a segment (n = 4, phase 0.5 → a square section). */
  phase?: number;
}

export class MeshBuilder {
  private tris: Tri[] = [];
  private hullPos: number[] = [];
  private hullNrm: number[] = [];
  private hullSkin: number[] = [];
  private hullIdx: number[] = [];
  private partStart = 0;
  min: V3 = [1e9, 1e9, 1e9];
  max: V3 = [-1e9, -1e9, -1e9];

  get triangleCount(): number { return this.tris.length; }

  beginPart(): void { this.partStart = this.tris.length; }

  /** Flags edges inside the current part whose dihedral angle exceeds `deg` (art §4.1-G generator rule). */
  autoEdges(deg: number, lw = 1): void {
    const cosT = Math.cos((deg * Math.PI) / 180);
    const key = (a: V3): string => `${Math.round(a[0] * 2000)},${Math.round(a[1] * 2000)},${Math.round(a[2] * 2000)}`;
    const map = new Map<string, Array<{ t: number; e: number }>>();
    for (let t = this.partStart; t < this.tris.length; t++) {
      const p = this.tris[t].p;
      for (let e = 0; e < 3; e++) {
        // edge opposite corner e: between corners (e+1)%3 and (e+2)%3
        const ka = key(p[(e + 1) % 3]), kb = key(p[(e + 2) % 3]);
        const k = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
        const arr = map.get(k) ?? [];
        arr.push({ t, e });
        map.set(k, arr);
      }
    }
    for (const arr of map.values()) {
      if (arr.length !== 2) continue;
      const n0 = this.triNormal(arr[0].t), n1 = this.triNormal(arr[1].t);
      if (dot(n0, n1) < cosT) {
        for (const { t, e } of arr) { this.tris[t].edge |= 1 << e; this.tris[t].lw = Math.max(this.tris[t].lw, lw); }
      }
    }
  }

  private triNormal(t: number): V3 {
    const p = this.tris[t].p;
    return norm(cross(sub(p[1], p[0]), sub(p[2], p[0])));
  }

  /** One flat triangle; orientation is fixed so its normal points along `outward` (if given). */
  tri(p0: V3, p1: V3, p2: V3, o: { mat: number; region: number; skin: Skin | [Skin, Skin, Skin]; pat?: [[number, number], [number, number], [number, number]]; edge?: number; lw?: number; outward?: V3 }): void {
    const skins: [Skin, Skin, Skin] = Array.isArray(o.skin) ? o.skin : [o.skin, o.skin, o.skin];
    let P: [V3, V3, V3] = [p0, p1, p2];
    let pat = o.pat ?? [[0, 0], [0, 0], [0, 0]];
    let edge = o.edge ?? 0;
    if (o.outward) {
      const n = cross(sub(p1, p0), sub(p2, p0));
      if (dot(n, o.outward) < 0) {
        P = [p0, p2, p1];
        pat = [pat[0], pat[2], pat[1]];
        skins.splice(1, 2, skins[2], skins[1]);
        // edges opposite corners 1 and 2 swap
        edge = (edge & 1) | ((edge & 2) << 1) | ((edge & 4) >> 1);
      }
    }
    if (len(cross(sub(P[1], P[0]), sub(P[2], P[0]))) < 1e-9) return;
    this.tris.push({ p: P, pat, skin: skins, mat: o.mat, region: o.region, edge, lw: o.lw ?? 1 });
    for (const q of P) for (let i = 0; i < 3; i++) { this.min[i] = Math.min(this.min[i], q[i]); this.max[i] = Math.max(this.max[i], q[i]); }
  }

  private hullVertex(p: V3, n: V3, s: Skin, region: number, w: number): number {
    const i = this.hullPos.length / 3;
    this.hullPos.push(p[0], p[1], p[2]);
    this.hullNrm.push(n[0], n[1], n[2], w);
    this.hullSkin.push(s.a, s.b, Math.round(s.w * 255), region);
    return i;
  }

  /** A loft (art §5.1): rings around a spine, flat facets, optional ring stagger and caps; plus its hull. */
  loft(L: LoftSpec): void {
    const n = L.sides, R = L.rings;
    const seed = L.seed ?? 1;
    // ring frames
    const pts: V3[][] = [];
    const vs: number[][] = [];
    let dist = 0;
    const us: number[] = [];
    for (let i = 0; i < R.length; i++) {
      if (i > 0) dist += len(sub(R[i].c, R[i - 1].c));
      us.push(dist / 2);
      const t = norm(i === 0 ? sub(R[1].c, R[0].c) : i === R.length - 1 ? sub(R[i].c, R[i - 1].c) : sub(R[i + 1].c, R[i - 1].c));
      let U = sub(L.up, mul(t, dot(L.up, t)));
      if (len(U) < 1e-4) U = sub(L.front, mul(t, dot(L.front, t)));
      U = norm(U);
      let F = sub(L.front, mul(t, dot(L.front, t)));
      F = sub(F, mul(U, dot(F, U)));
      if (len(F) < 1e-4) F = cross(t, U);
      F = norm(F);
      const ring: V3[] = [];
      const rv: number[] = [];
      const off = (L.stagger && i % 2 === 1 ? 0.5 : 0) + (L.phase ?? 0);
      for (let j = 0; j < n; j++) {
        const v = (j + off) / n;
        const a = v * Math.PI * 2;
        const k = R[i].shape ? R[i].shape!(v) : 1;
        const side = Math.cos(a) * R[i].ru * k, fr = Math.sin(a) * R[i].rf * k;
        let p = add(add(R[i].c, mul(U, side)), mul(F, fr));
        if (R[i].bump) p = add(p, R[i].bump!(v));
        const jit = L.jitter ?? 0.03;
        if (jit > 0) {
          // symmetric jitter: hash of |x| side so mirrored vertices move alike
          const sx = L.mirrorX ? Math.round(Math.abs(p[0]) * 400) : Math.round(p[0] * 400);
          const h = hash3(seed * 131 + i, sx, Math.round(p[1] * 400) + Math.round(p[2] * 97));
          const rr = Math.max(R[i].ru, R[i].rf) * jit * (h * 2 - 1);
          const radial = norm(sub(p, R[i].c));
          p = add(p, mul(radial, rr));
        }
        ring.push(p);
        rv.push(v);
      }
      pts.push(ring);
      vs.push(rv);
    }
    const centreOf = (i: number): V3 => R[i].c;
    const lw = L.lineWeight ?? 1;
    const matOf = (i: number, j: number): number => { const m = L.matAt ? L.matAt(i, j) : -1; return m >= 0 ? m : L.mat; };
    for (let i = 0; i < R.length - 1; i++) {
      const c = mul(add(centreOf(i), centreOf(i + 1)), 0.5);
      const hemA = L.hemRings?.includes(i) ?? false;
      const hemB = L.hemRings?.includes(i + 1) ?? false;
      for (let j = 0; j < n; j++) {
        const j1 = (j + 1) % n;
        const a = pts[i][j], b = pts[i][j1], cc = pts[i + 1][j1], d = pts[i + 1][j];
        const va = vs[i][j], vb = j1 === 0 ? 1 + vs[i][0] : vs[i][j1], vcc = j1 === 0 ? 1 + vs[i + 1][0] : vs[i + 1][j1], vd = vs[i + 1][j];
        const uA = us[i], uB = us[i + 1];
        const mat = matOf(i, j);
        const sA = R[i].skin, sB = R[i + 1].skin;
        const out1 = sub(mul(add(add(a, b), d), 1 / 3), c);
        const out2 = sub(mul(add(add(b, cc), d), 1 / 3), c);
        if (L.stagger && i % 2 === 0) {
          // ring i+1 is rotated by +half a segment: triangles (a, b, d) and (b, cc, d)
          this.tri(a, b, d, { mat, region: L.region, skin: [sA, sA, sB], pat: [[uA, va], [uA, vb], [uB, vd]], outward: out1, lw, edge: hemA ? 4 : 0 });
          this.tri(b, cc, d, { mat, region: L.region, skin: [sA, sB, sB], pat: [[uA, vb], [uB, vcc], [uB, vd]], outward: out2, lw, edge: hemB ? 1 : 0 });
        } else if (L.stagger) {
          // ring i is rotated: triangles (a, cc, d) and (a, b, cc)
          this.tri(a, cc, d, { mat, region: L.region, skin: [sA, sB, sB], pat: [[uA, va], [uB, vcc], [uB, vd]], outward: out1, lw, edge: hemB ? 1 : 0 });
          this.tri(a, b, cc, { mat, region: L.region, skin: [sA, sA, sB], pat: [[uA, va], [uA, vb], [uB, vcc]], outward: out2, lw, edge: hemA ? 4 : 0 });
        } else {
          this.tri(a, b, cc, { mat, region: L.region, skin: [sA, sA, sB], pat: [[uA, va], [uA, vb], [uB, vcc]], outward: out1, lw, edge: hemA ? 4 : 0 });
          this.tri(a, cc, d, { mat, region: L.region, skin: [sA, sB, sB], pat: [[uA, va], [uB, vcc], [uB, vd]], outward: out2, lw, edge: hemB ? 1 : 0 });
        }
      }
    }
    const cap = (i: number, dir: number): void => {
      const ring = pts[i];
      let cen: V3 = [0, 0, 0];
      for (const p of ring) cen = add(cen, p);
      cen = mul(cen, 1 / n);
      const t = norm(sub(R[Math.min(R.length - 1, i + (dir < 0 ? 1 : 0))].c, R[Math.max(0, i - (dir < 0 ? 0 : 1))].c));
      const tip = add(cen, mul(t, dir * Math.min(R[i].ru, R[i].rf) * 0.25));
      for (let j = 0; j < n; j++) {
        const j1 = (j + 1) % n;
        this.tri(ring[j], ring[j1], tip, { mat: matOf(i, j), region: L.region, skin: R[i].skin, pat: [[us[i], vs[i][j]], [us[i], vs[i][j1]], [us[i], 0]], outward: mul(t, dir), lw });
      }
    };
    if (L.capStart) cap(0, -1);
    if (L.capEnd) cap(R.length - 1, 1);
    if (L.noHull) return;
    // hull: shared ring vertices with smooth radial normals, closed by cap fans
    const hw = L.hullWeight ?? 1;
    const base = this.hullPos.length / 3;
    for (let i = 0; i < R.length; i++) {
      const t = norm(i === 0 ? sub(R[1].c, R[0].c) : i === R.length - 1 ? sub(R[i].c, R[i - 1].c) : sub(R[i + 1].c, R[i - 1].c));
      for (let j = 0; j < n; j++) {
        let nr = norm(sub(pts[i][j], R[i].c));
        if (i === 0 && L.capStart) nr = norm(add(nr, mul(t, -0.6)));
        if (i === R.length - 1 && L.capEnd) nr = norm(add(nr, mul(t, 0.6)));
        this.hullVertex(pts[i][j], nr, R[i].skin, L.region, hw);
      }
    }
    const H = (i: number, j: number): number => base + i * n + (j % n);
    const pushOriented = (ia: number, ib: number, ic: number, outward: V3): void => {
      const P = (k: number): V3 => [this.hullPos[k * 3], this.hullPos[k * 3 + 1], this.hullPos[k * 3 + 2]];
      const nn = cross(sub(P(ib), P(ia)), sub(P(ic), P(ia)));
      if (dot(nn, outward) >= 0) this.hullIdx.push(ia, ib, ic); else this.hullIdx.push(ia, ic, ib);
    };
    for (let i = 0; i < R.length - 1; i++) {
      const c = mul(add(R[i].c, R[i + 1].c), 0.5);
      for (let j = 0; j < n; j++) {
        const pa = pts[i][j];
        const out = sub(pa, c);
        pushOriented(H(i, j), H(i, j + 1), H(i + 1, j + 1), out);
        pushOriented(H(i, j), H(i + 1, j + 1), H(i + 1, j), out);
      }
    }
    const hullCap = (i: number, dir: number): void => {
      let cen: V3 = [0, 0, 0];
      for (const p of pts[i]) cen = add(cen, p);
      cen = mul(cen, 1 / n);
      const t = norm(dir < 0 ? sub(R[0].c, R[1].c) : sub(R[i].c, R[i - 1].c));
      const ci = this.hullVertex(add(cen, mul(t, Math.min(R[i].ru, R[i].rf) * 0.25)), t, R[i].skin, L.region, hw);
      for (let j = 0; j < n; j++) pushOriented(H(i, j), H(i, j + 1), ci, t);
    };
    hullCap(0, -1);
    hullCap(R.length - 1, 1);
  }

  /**
   * A flat prism: a convex-ish 2D outline (CCW, in the plane spanned by `ax`, `ay` around `origin`) extruded by
   * `thick` along `az` (front = +az). Front and back faces are fans from the centroid; sides are quads. Hull included.
   */
  prism(outline: Array<[number, number]>, o: { origin: V3; ax: V3; ay: V3; az: V3; thick: number; matFront: number; matBack?: number; matSide?: number; region: number; skin: Skin; frontEdges?: boolean; lw?: number; noHull?: boolean; hullWeight?: number }): void {
    const P = (u: number, v: number, w: number): V3 => add(add(add(o.origin, mul(o.ax, u)), mul(o.ay, v)), mul(o.az, w));
    const n = outline.length;
    let cu = 0, cv = 0;
    for (const [u, v] of outline) { cu += u; cv += v; }
    cu /= n; cv /= n;
    const h = o.thick / 2;
    const lw = o.lw ?? 1;
    for (let j = 0; j < n; j++) {
      const [u0, v0] = outline[j], [u1, v1] = outline[(j + 1) % n];
      const f0 = P(u0, v0, h), f1 = P(u1, v1, h), b0 = P(u0, v0, -h), b1 = P(u1, v1, -h);
      this.tri(P(cu, cv, h), f0, f1, { mat: o.matFront, region: o.region, skin: o.skin, outward: o.az, pat: [[0.5, 0.5], [0.5 + u0, 0.5 + v0], [0.5 + u1, 0.5 + v1]], edge: o.frontEdges ? 1 : 0, lw });
      this.tri(P(cu, cv, -h), b1, b0, { mat: o.matBack ?? o.matFront, region: o.region, skin: o.skin, outward: mul(o.az, -1), lw });
      const side = norm(sub(mul(add(f0, f1), 0.5), P(cu, cv, h)));
      this.tri(f0, b0, b1, { mat: o.matSide ?? o.matFront, region: o.region, skin: o.skin, outward: side, lw });
      this.tri(f0, b1, f1, { mat: o.matSide ?? o.matFront, region: o.region, skin: o.skin, outward: side, lw });
    }
    if (o.noHull) return;
    const hw = o.hullWeight ?? 1;
    const base = this.hullPos.length / 3;
    for (let j = 0; j < n; j++) {
      const [u, v] = outline[j];
      const radial = norm(add(mul(o.ax, u - cu), mul(o.ay, v - cv)));
      this.hullVertex(P(u, v, h), norm(add(radial, mul(o.az, 0.5))), o.skin, o.region, hw);
      this.hullVertex(P(u, v, -h), norm(add(radial, mul(o.az, -0.5))), o.skin, o.region, hw);
    }
    const cf = this.hullVertex(P(cu, cv, h), o.az, o.skin, o.region, hw);
    const cb = this.hullVertex(P(cu, cv, -h), mul(o.az, -1), o.skin, o.region, hw);
    const HP = (k: number): V3 => [this.hullPos[k * 3], this.hullPos[k * 3 + 1], this.hullPos[k * 3 + 2]];
    const push = (a: number, b: number, c: number, out: V3): void => {
      const nn = cross(sub(HP(b), HP(a)), sub(HP(c), HP(a)));
      if (dot(nn, out) >= 0) this.hullIdx.push(a, b, c); else this.hullIdx.push(a, c, b);
    };
    for (let j = 0; j < n; j++) {
      const j1 = (j + 1) % n;
      const fa = base + j * 2, ba = fa + 1, fb = base + j1 * 2, bb = fb + 1;
      const out = norm(sub(mul(add(HP(fa), HP(fb)), 0.5), P(cu, cv, 0)));
      push(fa, ba, bb, out); push(fa, bb, fb, out);
      push(cf, fa, fb, o.az); push(cb, bb, ba, mul(o.az, -1));
    }
  }

  /** Merges another builder's triangles with a transform applied to positions (rigidly re-skinned to `skin`). */
  addTransformed(other: MeshBuilder, m: (p: V3) => V3, skin?: Skin): void {
    for (const t of other.tris) {
      const p: [V3, V3, V3] = [m(t.p[0]), m(t.p[1]), m(t.p[2])];
      this.tris.push({ ...t, p, skin: skin ? [skin, skin, skin] : t.skin });
      for (const q of p) for (let i = 0; i < 3; i++) { this.min[i] = Math.min(this.min[i], q[i]); this.max[i] = Math.max(this.max[i], q[i]); }
    }
    const base = this.hullPos.length / 3;
    for (let i = 0; i < other.hullPos.length / 3; i++) {
      const p = m([other.hullPos[i * 3], other.hullPos[i * 3 + 1], other.hullPos[i * 3 + 2]]);
      const o = m([0, 0, 0]);
      const q = sub(m([other.hullNrm[i * 4], other.hullNrm[i * 4 + 1], other.hullNrm[i * 4 + 2]]), o);
      this.hullPos.push(p[0], p[1], p[2]);
      const nq = norm(q);
      this.hullNrm.push(nq[0], nq[1], nq[2], other.hullNrm[i * 4 + 3]);
      if (skin) this.hullSkin.push(skin.a, skin.b, Math.round(skin.w * 255), other.hullSkin[i * 4 + 3]);
      else this.hullSkin.push(other.hullSkin[i * 4], other.hullSkin[i * 4 + 1], other.hullSkin[i * 4 + 2], other.hullSkin[i * 4 + 3]);
    }
    for (const k of other.hullIdx) this.hullIdx.push(base + k);
  }

  /** Packs the art §3.2 render mesh (24 B) and hull (20 B + u16 indices). */
  build(lod: 0 | 1 | 'hero' = 0): CreatureMeshData {
    const nv = this.tris.length * 3;
    const vb = new ArrayBuffer(nv * 24);
    const dv = new DataView(vb);
    let o = 0;
    const clamp16 = (x: number): number => Math.max(0, Math.min(65535, Math.round(x * 65535)));
    for (const t of this.tris) {
      for (let k = 0; k < 3; k++) {
        const p = t.p[k], s = t.skin[k], pat = t.pat[k];
        dv.setFloat32(o, p[0], true); dv.setFloat32(o + 4, p[1], true); dv.setFloat32(o + 8, p[2], true);
        dv.setUint16(o + 12, clamp16(Math.min(1, Math.max(0, pat[0]))), true);
        dv.setUint16(o + 14, clamp16(Math.min(1, Math.max(0, pat[1]))), true);
        dv.setUint8(o + 16, s.a); dv.setUint8(o + 17, s.b); dv.setUint8(o + 18, Math.round(Math.min(1, Math.max(0, s.w)) * 255));
        dv.setUint8(o + 19, Math.round(Math.min(2, t.lw) * 127.5));
        dv.setUint8(o + 20, t.mat); dv.setUint8(o + 21, t.region); dv.setUint8(o + 22, t.edge & 7); dv.setUint8(o + 23, k);
        o += 24;
      }
    }
    const nh = this.hullPos.length / 3;
    const hb = new ArrayBuffer(nh * 20);
    const hv = new DataView(hb);
    for (let i = 0; i < nh; i++) {
      const q = i * 20;
      hv.setFloat32(q, this.hullPos[i * 3], true); hv.setFloat32(q + 4, this.hullPos[i * 3 + 1], true); hv.setFloat32(q + 8, this.hullPos[i * 3 + 2], true);
      for (let k = 0; k < 4; k++) hv.setInt8(q + 12 + k, Math.round(Math.max(-1, Math.min(1, this.hullNrm[i * 4 + k])) * 127));
      for (let k = 0; k < 4; k++) hv.setUint8(q + 16 + k, this.hullSkin[i * 4 + k]);
    }
    if (nh > 65535) throw new Error('hull exceeds u16 indices');
    const indices = new Uint16Array(this.hullIdx);
    return {
      vertices: vb, vertexCount: nv,
      hull: { vertices: hb, indices, vertexCount: nh, indexCount: indices.length },
      bounds: { min: [...this.min], max: [...this.max] }, points: {}, lod,
      tris: { render: this.tris.length, hull: indices.length / 3 },
    };
  }
}
